import ClinicalActionPanel from "@/features/clinical-actions/ClinicalActionPanel.jsx";
import { loadClinicalAction } from "@/services/clinicalActionService.js";
// src/components/referrals/ReferralFormCard.jsx //
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "@/components/ui/Card.jsx";
import supabase from "@/services/supabaseClient";
import Button from "@/components/ui/Button.jsx";
import AvatarThumb44 from "@/sot/primitives/AvatarThumb44.jsx";
import SignatureStampLine from "@/components/prescriptions/SignatureStampLine.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { resolveDoctorAvatarSrc } from "@/lib/doctorsDirectory.js";
import { resolveCanonicalClinicianDisplayName } from "@/pages/shared/profileAddressedName.js";
import {
  selectDoctorLicenseNumber,
  selectDoctorPrescriptionName,
} from "@/utils/prescriptionDoctorIdentity.js";
import {
  createAndPresentReferralPackage,
  loadEligibleReferralSpecialists,
} from "@/services/referralPackageService.js";

const REQUEST_OPTIONS = [
  { value: "Consultation and diagnosis", labelKey: "referrals.form.request.consultationDiagnosis", fallback: "Consultation and diagnosis" },
  { value: "Second opinion", labelKey: "referrals.form.request.secondOpinion", fallback: "Second opinion" },
  { value: "Transfer of care", labelKey: "referrals.form.request.transferOfCare", fallback: "Transfer of care" },
  { value: "Procedure / investigation", labelKey: "referrals.form.request.procedureInvestigation", fallback: "Procedure / investigation" },
  { value: "Other", labelKey: "referrals.form.request.other", fallback: "Other" },
];

const URGENCY_OPTIONS = [
  { value: "routine", label: "Routine" },
  { value: "soon", label: "Soon" },
  { value: "urgent", label: "Urgent" },
];

const REFERRAL_SIGNATURE_PLACEHOLDER = "/icons/appointments/e-prescription/Signature.svg";
const REFERRAL_STAMP_BASE = "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png";

function todayLabel() {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

function buildDraftStorageKey(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  return cleanAppointmentId
    ? `afrimed.referralDraft.${cleanAppointmentId}`
    : "afrimed.referralDraft";
}

function referralSpecialtyDiscoveryContext(value) {
  const candidate = String(value || "").trim();
  const normalized = candidate.toLowerCase().replace(/\./g, "").trim();
  // The Specialty field is free text today. Never turn a salutation or an
  // explicitly name-shaped entry into a specialty filter on Referral Network.
  if (!candidate || /^(dr|doctor|prof|mr|mrs|ms)$/i.test(normalized) || /^(dr|doctor|prof|mr|mrs|ms)\.?\s+/i.test(candidate)) return "";
  return candidate;
}

export default function ReferralFormCard({
  appointmentId,
  patientUserId,
  doctorUserId,
  onSuccess,
}) {
  const { t, formatDate } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const clinicalActionId = new URLSearchParams(location.search).get("clinicalActionId");
  const draftStorageKey = React.useMemo(
    () => buildDraftStorageKey(appointmentId),
    [appointmentId]
  );
  const [dateLabel] = React.useState(() =>
    formatDate(new Date(), {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }) || todayLabel()
  );
  const [specialty, setSpecialty] = React.useState("");
  const [specialists, setSpecialists] = React.useState([]);
  const [selectedSpecialistId, setSelectedSpecialistId] = React.useState("");
  const [specialistsLoading, setSpecialistsLoading] = React.useState(false);
  const [clinicalHistory, setClinicalHistory] = React.useState("");
  const [specificRequest, setSpecificRequest] = React.useState("Consultation and diagnosis");
  const [otherRequest, setOtherRequest] = React.useState("");
  const [urgency, setUrgency] = React.useState("routine");
  const [saving, setSaving] = React.useState(false);
  const [prepared, setPrepared] = React.useState(false);

  const [signatureUrl, setSignatureUrl] = React.useState("");
  const [signatureLoading, setSignatureLoading] = React.useState(true);
  const [referringClinician, setReferringClinician] = React.useState(null);
  const [draftHydrated, setDraftHydrated] = React.useState(false);
  React.useEffect(() => {
    if (!clinicalActionId) return;
    let alive = true;
    loadClinicalAction(clinicalActionId).then(action => {
      if (alive && action.appointment_id === appointmentId && action.kind === 'referral' && action.state === 'reviewed') setClinicalHistory(action.content.text || '');
    }).catch(() => {});
    return () => { alive = false; };
  }, [clinicalActionId, appointmentId]);


  React.useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const rawDraft = window.localStorage.getItem(draftStorageKey);
      if (rawDraft) {
        const draft = JSON.parse(rawDraft);
        if (draft && typeof draft === "object") {
          setSpecialty(String(draft.specialty || ""));
          setSelectedSpecialistId(String(draft.selectedSpecialistId || ""));
          setClinicalHistory(String(draft.clinicalHistory || ""));
          setSpecificRequest(
            String(draft.specificRequest || "Consultation and diagnosis")
          );
          setOtherRequest(String(draft.otherRequest || ""));
          setUrgency(String(draft.urgency || "routine"));
        }
      }
    } catch (error) {
      console.error("[ReferralFormCard] draft restore failed:", error);
    } finally {
      setDraftHydrated(true);
    }
  }, [draftStorageKey]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!draftHydrated) return;

    try {
      window.localStorage.setItem(
        draftStorageKey,
        JSON.stringify({
          specialty,
          selectedSpecialistId,
          clinicalHistory,
          specificRequest,
          otherRequest,
          urgency,
        })
      );
    } catch (error) {
      console.error("[ReferralFormCard] draft persist failed:", error);
    }
  }, [
    draftStorageKey,
    draftHydrated,
    specialty,
    selectedSpecialistId,
    clinicalHistory,
    specificRequest,
    otherRequest,
    urgency,
  ]);

  React.useEffect(() => {
    let alive = true;

    async function loadSignature() {
      if (!doctorUserId) {
        if (!alive) return;
        setSignatureLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("doctor_profiles")
          .select("full_name, first_name, last_name, salutation, register_number, medical_license_number, signature_path")
          .eq("user_id", doctorUserId)
          .maybeSingle();

        if (error) throw error;
        if (alive) setReferringClinician(data || null);

        const path = data?.signature_path || "";
        if (!path) {
          if (!alive) return;
          setSignatureLoading(false);
          return;
        }

        const { data: signed, error: signedErr } = await supabase.storage
          .from("doctor-assets")
          .createSignedUrl(path, 60 * 10);

        if (signedErr) throw signedErr;

        if (!alive) return;
        setSignatureUrl(signed?.signedUrl || "");
      } catch (e) {
        console.error("[ReferralFormCard] signature load failed:", e?.message || e);
      } finally {
        if (alive) setSignatureLoading(false);
      }
    }

    loadSignature();

    return () => {
      alive = false;
    };
  }, [doctorUserId]);

  React.useEffect(() => {
    let alive = true;
    async function loadSpecialists() {
      if (!specialty.trim()) {
        if (alive) {
          setSpecialists([]);
          setSpecialistsLoading(false);
        }
        return;
      }
      setSpecialistsLoading(true);
      try {
        const rows = await loadEligibleReferralSpecialists({
          specialty,
          excludeUserId: doctorUserId,
        });
        if (alive) setSpecialists(rows);
      } catch (error) {
        console.error("[ReferralFormCard] specialist directory load failed:", error);
        if (alive) setSpecialists([]);
      } finally {
        if (alive) setSpecialistsLoading(false);
      }
    }
    void loadSpecialists();
    return () => { alive = false; };
  }, [specialty, doctorUserId]);

  const selectedSpecialist = React.useMemo(
    () => specialists.find((entry) => String(entry?.doctor_user_id || "") === selectedSpecialistId) || null,
    [specialists, selectedSpecialistId],
  );

  async function handleSubmit() {
    if (!specialty.trim()) {
      alert(t("referrals.form.errors.specialtyRequired", "Please enter the specialty for this referral."));
      return;
    }

    if (!clinicalHistory.trim()) {
      alert(t("referrals.form.errors.clinicalHistoryRequired", "Please enter the clinical history."));
      return;
    }
    if (!selectedSpecialist) {
      alert(t("referrals.form.errors.specialistRequired", "Select an eligible AfriMed specialist."));
      return;
    }

    setSaving(true);

    try {
      const resolvedSpecificRequest =
        specificRequest === "Other" ? otherRequest.trim() : specificRequest;

      if (!resolvedSpecificRequest) {
        alert(t("referrals.form.errors.specificRequestRequired", "Please complete the specific request."));
        return;
      }

      const payload = {
        referral_type: "specialty",
        to_specialty_label: specialty.trim(),
        note_text: String(selectedSpecialist.display_name || "").trim() || null,
        reason: clinicalHistory.trim(),
        clinical_history: clinicalHistory.trim(),
        specific_request: resolvedSpecificRequest,
        urgency,
        delivered_to_patient_at: new Date().toISOString(),
      };

      if (!patientUserId || !doctorUserId || !appointmentId) {
        alert(t("referrals.form.errors.createFailed", "Failed to create referral"));
        return;
      }

      const result = await createAndPresentReferralPackage({
        clinicalActionId,
        referral: payload,
        appointmentId,
        patientUserId,
        referringDoctorUserId: doctorUserId,
        specialist: selectedSpecialist,
        clinicalHistory: clinicalHistory.trim(),
        specificRequest: resolvedSpecificRequest,
        urgency,
      });

      if (typeof window !== "undefined") {
        window.localStorage.removeItem(draftStorageKey);
      }

      setPrepared(true);
      onSuccess?.(result.referral);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.info("[ReferralPackagePreparation]", {
          event: "prepare_failed",
          appointmentId,
          patientUserId,
          receivingDoctorUserId: selectedSpecialist?.doctor_user_id || null,
          code: error?.code || null,
          message: error?.message || "unknown_error",
        });
      }
      alert(t("referrals.form.errors.createFailed", "Failed to create referral"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <ClinicalActionPanel appointmentId={appointmentId} kinds={['referral']} onReviewed={action => {
        setClinicalHistory(action.content.text || '');
        const query = new URLSearchParams(location.search);
        query.set('clinicalActionId', action.id);
        navigate({pathname: location.pathname, search: query.toString()}, {replace: true});
      }} />
      {/* Referral destination */}
      <section className="space-y-3">
        <div>
          <div className="text-title-medium text-on-surface">
            {t("referrals.form.destination.title", "Referral destination")}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {t(
              "referrals.form.destination.subtitle",
              "Direct the patient to the appropriate level of care."
            )}
          </div>
        </div>

        <Card className="px-4 py-3">
          <div className="text-label-medium text-on-surface-var">
            {t("referrals.summary.date", "Date")}
          </div>
          <div className="mt-1 text-body-large text-on-surface">{dateLabel}</div>
        </Card>

        {selectedSpecialist ? <div className="flex items-center gap-3 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-3 py-3"><AvatarThumb44 size={40} shape="circle" role="doctor" src={resolveDoctorAvatarSrc(selectedSpecialist)} alt="" /><div className="min-w-0 flex-1"><div className="text-body-large text-on-surface">{resolveCanonicalClinicianDisplayName(selectedSpecialist, "Clinician")}</div><div className="truncate text-body-small text-on-surface-var">{[selectedSpecialist.specialty, selectedSpecialist.facility_name || selectedSpecialist.facility_city || selectedSpecialist.facility_country].filter(Boolean).join(" · ")}</div></div><Button size="sm" variant="text" onClick={() => setSelectedSpecialistId("")}>{t("referrals.form.destination.change", "Change")}</Button></div> : <input value={specialty} onChange={(e) => { setSpecialty(e.target.value); setSelectedSpecialistId(""); }} placeholder={t("referrals.form.destination.specialtyPlaceholder", "Specialty (e.g. Cardiology)")} className="w-full rounded-[12px] border border-transparent bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-large text-on-surface outline-none" />}

        <div className="space-y-2">
          <div className="text-label-medium text-on-surface-var">
            {t("referrals.form.destination.specialist", "Eligible specialist")}
          </div>
          {!selectedSpecialist && specialistsLoading ? <div className="text-body-small text-on-surface-var">{t("referrals.form.destination.loadingSpecialists", "Loading eligible specialists…")}</div> : null}
          {!selectedSpecialist && specialty.trim() && !specialistsLoading && specialists.length === 0 ? <div className="text-body-small text-on-surface-var">{t("referrals.form.destination.noSpecialists", "No connected specialists available for this specialty.")}</div> : null}
          {!selectedSpecialist && specialists.map((specialist) => {
            const active = specialist.doctor_user_id === selectedSpecialistId;
            const detail = [specialist.specialty, specialist.facility_name || specialist.facility_city].filter(Boolean).join(" · ");
            return <button key={specialist.doctor_user_id} type="button" onClick={() => setSelectedSpecialistId(specialist.doctor_user_id)} className={["flex w-full items-center gap-3 rounded-[12px] px-3 py-3 text-left", active ? "bg-[var(--state-primary-12)] ring-1 ring-[rgb(var(--afrimed-primary))]" : "bg-[rgb(var(--clr-surface-cont-highest))]"].join(" ")}>
              <AvatarThumb44
                size={40}
                shape="circle"
                role="doctor"
                src={resolveDoctorAvatarSrc(specialist)}
                alt=""
              />
              <span className="min-w-0"><span className="block text-body-large text-on-surface">{resolveCanonicalClinicianDisplayName(specialist, "Clinician")}</span><span className="block truncate text-body-small text-on-surface-var">{detail || t("referrals.form.destination.clinician", "Clinician")}</span></span>
            </button>;
          })}
          <div className="text-body-small text-on-surface-var">{t("referrals.form.destination.specialistHelper", "Selecting a specialist does not grant access. The patient reviews this package before anything is shared.")}</div>
        </div>
        <button type="button" onClick={() => { const referralSpecialty = referralSpecialtyDiscoveryContext(specialty); navigate("/doctors/referral-network", { state: referralSpecialty ? { referralSpecialty } : undefined }); }} className="inline-flex rounded-full bg-[var(--state-primary-12)] px-3 py-2 text-label-medium text-[rgb(var(--afrimed-primary))]">{t("referrals.form.destination.findSpecialists", "Find specialists")}</button>
      </section>

      {/* Clinical history */}
      <section className="space-y-3">
        <div>
          <div className="text-title-medium text-on-surface">
            {t("referrals.form.clinicalHistory.title", "Clinical history")}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {t(
              "referrals.form.clinicalHistory.subtitle",
              "Record symptoms, findings, relevant medication, tests, and context."
            )}
          </div>
        </div>

        <textarea
          value={clinicalHistory}
          onChange={(e) => setClinicalHistory(e.target.value)}
          placeholder={t(
            "referrals.form.clinicalHistory.placeholder",
            "Symptoms, findings, relevant medication, labs, and clinical context."
          )}
          className="
            w-full min-h-[160px] rounded-[12px]
            border border-transparent
            bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3
            text-body-medium text-on-surface outline-none
          "
        />
      </section>

      {/* Specific request */}
      <section className="space-y-3">
        <div>
          <div className="text-title-medium text-on-surface">
            {t("referrals.form.request.title", "Specific request")}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {t("referrals.form.request.subtitle", "Clarify the purpose of this referral.")}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {REQUEST_OPTIONS.map((option) => {
            const active = specificRequest === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSpecificRequest(option.value)}
                className={[
                  "rounded-full px-4 py-2 text-label-medium transition-colors",
                  active
                    ? "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]"
                    : "border border-[rgba(var(--clr-outline-variant),0.9)] bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface",
                ].join(" ")}
              >
                {t(option.labelKey, option.fallback)}
              </button>
            );
          })}
        </div>

        {specificRequest === "Other" ? (
          <input
            value={otherRequest}
            onChange={(e) => setOtherRequest(e.target.value)}
            placeholder={t("referrals.form.request.otherPlaceholder", "Enter the specific request")}
            className="
              w-full rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.9)]
              bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3
              text-body-large text-on-surface outline-none
            "
          />
        ) : null}
      </section>

      {/* Urgency */}
      <section className="space-y-3">
        <div>
          <div className="text-title-medium text-on-surface">
            {t("referrals.form.urgency.title", "Urgency")}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {t(
              "referrals.form.urgency.subtitle",
              "Indicate how quickly the patient should be seen."
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {URGENCY_OPTIONS.map((option) => {
            const active = urgency === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setUrgency(option.value)}
                className={[
                  "rounded-full px-4 py-2 text-label-medium transition-colors",
                  active
                    ? "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]"
                    : "border border-[rgba(var(--clr-outline-variant),0.9)] bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface",
                ].join(" ")}
              >
                {t(`referrals.form.urgency.${option.value}`, option.label)}
              </button>
            );
          })}
        </div>
      </section>

      {/* Physician signature */}
      <section className="space-y-3">
        <div>
          <div className="text-title-medium text-on-surface">
            {t("referrals.form.signature.title", "Physician signature")}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {t(
              "referrals.form.signature.subtitle",
              "The saved doctor signature will be attached to this referral."
            )}
          </div>
        </div>

        <div className="pt-1">
          {signatureLoading ? (
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.form.signature.loading", "Loading signature…")}
            </div>
          ) : (
            <>
              <div className="text-body-small text-on-surface-var">
                {t("referrals.form.signature.referringClinician", "Referring clinician")}
              </div>
              <div className="mt-1 text-body-medium text-on-surface">
                {selectDoctorPrescriptionName(referringClinician || {}) || "Clinician"}
              </div>
              {selectDoctorLicenseNumber(referringClinician || {}) ? (
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t("referrals.form.signature.registration", "Registration")}: {selectDoctorLicenseNumber(referringClinician || {})}
                </div>
              ) : null}
              <div className="-mt-1">
                <SignatureStampLine
                  sigSrc={signatureUrl || REFERRAL_SIGNATURE_PLACEHOLDER}
                  stampBaseSrc={REFERRAL_STAMP_BASE}
                  doctorName={selectDoctorPrescriptionName(referringClinician || {}) || "Referring clinician"}
                  regNo={selectDoctorLicenseNumber(referringClinician || {})}
                  stampSizePx={96}
                  sigClassName="h-[68px] w-[160px]"
                  sigNudgeY={2}
                  stampNudgeY={2}
                />
              </div>
              <div className="mt-3 text-body-small text-on-surface-var">
                {t(
                  "referrals.form.signature.poweredBy",
                  "Powered by AfriMed™. The referring clinician remains responsible for clinical decisions.",
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <div className="flex justify-center pt-3">
        <div className="w-full max-w-[352px]">
          {prepared ? <div className="mb-3 rounded-[12px] bg-[var(--state-positive-08)] px-4 py-3 text-body-medium text-on-surface">
            {t("referrals.form.prepared", "Referral prepared. Awaiting Patient approval.")}
          </div> : null}
          <Button fullWidth loading={saving} disabled={prepared || signatureLoading} onClick={handleSubmit}>
            {t("referrals.form.submitForPatientApproval", "Send for Patient approval")}
          </Button>
        </div>
      </div>
    </div>
  );
}
